/** @module csi/view/_query/AdHocFilterContainer */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/when",
	"dijit/_Container",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/form/Button",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/toolbar/MultiFilterCondition",
	"csi/widget/NameValuePair",
	"dojo/text!./AdHocFilterContainer.html"
], function(declare, domClass, when, _Container, _TemplatedMixin, _WidgetBase, Button, csi, _, html, locale, MultiFilterCondition, NameValuePair, template) {

	var CSS_SELECTED = "csi-selected";

	html.registerDataAttribute("DATA_AD_HOC_KEY", "adhoc-key");

	return declare([_WidgetBase, _TemplatedMixin, _Container], {
		baseClass: "csi-filter-container",
		templateString: template,

		/** @property {_QueryMixin} qm - object storing query filters */
		queryMixin: null,

		/** @property {boolean} bBatchFilter - show filters pills as "selectAll", used on BatchFilter */
		bBatchFilter: false,

		/** @property {Widget} placementAnchor - insert pills before this widget */
		// placementAnchor: Widget,

		/** @property {string[]} _keys - ordered list of query keys so that pill order is maintained */
		_keys: null,

		/**
		 * Extension point called when clearing all query filters
		 */
		onClearFilters: function() { },

		/**
		 * Extension point called when adding new filter
		 * @param {string} key - spec key
		 * @param {NameValuePair} [pillWidget] - pill widget
		 */
		onAddFilter: function(key, pillWidget) { },

		/**
		 * Extension point called when clicking on an existing filter
		 * @param {string} key - spec key
		 * @param {NameValuePair} [pillWidget] - pill widget
		 */
		onClickFilter: function(key, pillWidget) { },

		/**
		 * Extension point called when removing a single query filter
		 * @param {string} key - spec key
		 */
		onRemoveFilter: function(key) { },

		/**
		 * Mark filter for selected attribute with CSS class
		 * @param {string} key - spec key currently selected for filtering
		 */
		selectAttribute: function(key) {
			this._selectedKey = key;
			html.queryDom(["." + CSS_SELECTED], this.domNode).removeClass(CSS_SELECTED);
			html.queryDom([html.getDataAttributeSelector(html.DATA_AD_HOC_KEY, key)], this.domNode).addClass(CSS_SELECTED);
		},

		/**
		 * Listens for changes to overall query and recreates pills if filters exist, otherwise hides itself
			 * @param {Object} value
		 */
		_addFilter: function(key) {
			var qm = this.queryMixin;
			var spec = qm.getSpec(key);
			var value = qm.getQueryValueAsLabel(key, false);
			var name = spec ? spec.name : key;
			var css = (key == this._selectedKey) ? CSS_SELECTED : null;
			var title;
			var self = this;

			if (!this.bBatchFilter && (value.length > 3)) {
				title = new MultiFilterCondition({
					label: name,
					value: value
				});

				value = value.slice(0, 3);
				value.push("...");
			} else if (this.bBatchFilter && !value.length) {
				value = [locale.resource("SelectAll")];
			}

			var w = new NameValuePair({
				"class": css,
				iconCss: "material-icons csi-name-value-pair-close",
				iconText: "close",
				iconTitle: locale.resource("Remove"),
				key,
				name: name,
				title: title,
				titlePosition: ["below-centered", "above-centered"],
				value: value.join(", "),
				onClick: function() {
					self.onClickFilter(key, w);
				},
				onIcon: function() {
					self.onRemoveFilter(key, w);
				}
			});

			html.setDataAttribute(w.domNode, html.DATA_AD_HOC_KEY, key);
			const anchor = this.placementAnchor;
			if (anchor) {
				const idx = this.getIndexOfChild(anchor);
				this.addChild(w, idx);
			} else {
				this.addChild(w);
			}

			this.onAddFilter(key, w);
		},

		/**
		 * Listens for changes to overall query and recreates pills if filters exist, otherwise hides itself
		 */
		_onQuery: function() {
			// cannot render filters if no query mixin or spec store
			var qm = this.queryMixin;
			if (!qm || !qm.get("querySpecStore")) {
				return;
			}

			// let view finish loading, otherwise some stores might be not available and filter label might be not resolved (enums)
			when(qm.getDeferred?.(), () => {
				var query = qm.get("query");
				var self = this;

				if (_.isEmpty(query)) {
					// query is empty, so hide container
					this._keys = null;
					this._setVisible(false);
				} else {
					var keys = this._keys;
					var keyMap = {};

					if (keys) {
						// maintain list of keys so that order is maintained
						var hash = csi.hashArray(keys, null, Number);
						var n = keys.length;

						_.each(query, function(value, key) {
							keyMap[key] = (hash[key] == null) ? ++n : hash[key];
						});
					} else {
						// order keys based on order in view
						_.each(query, function(value, key) {
							keyMap[key] = qm.getSpec(key).getSpecsId();
						});
					}

					this._keys = keys = csi.sortByArray(_.keys(keyMap), _.values(keyMap));

					// render new query filters
					html.clear(this.containerNode, true);

					_.each(keys, function(key) {
						this._addFilter(key, query[key]);
					}, this);

					// render action to clear all filters
					this.addChild(new Button({
						"class": html.CSS_BUTTON_LINK,
						label: locale.resource("ClearAll"),
						onClick: function() {
							self.onClearFilters();
						}
					}));

					this._setVisible(true);
				}

				// notify view of possible change to DOM structure/spacing
				if (qm.resize) {
					qm.resize();
				}
			});
		},

		/**
		 * Map label to labelNode
		 */
		_setLabelAttr: {node: "labelNode", type: "innerText"},

		/**
		 * Reset container when queryMixin changes
		 * @param {_QueryMixin} qm
		 */
		_setQueryMixinAttr: function(qm) {
			this._set("queryMixin", qm);
			this._keys = null;

			if (qm) {
				// mark query mixin so we do not attach listeners more than once; the image browser has a query mixin per pane,
				// so need to be careful when user switches panes back and forth
				if (!qm._bFilterContainer) {
					qm._bFilterContainer = true;

					// listen for changes to query from queryMixin and render initial filters
					qm.watch("query", (name, oldValue, newValue) => {
						this._onQuery();
					});

					qm.watch("querySpecStore", (name, oldValue, newValue) => {
						if (newValue) {
							this._onQuery();
						}
					});
				}

				// refresh filters
				this._onQuery();
			} else {
				// hide if no query mixin
				this._setVisible(false);
			}
		},

		/**
		 * Show and hide widget - use CSS rather than styling so visibility can be overridden by parent widget
			 * @param {boolean} b
		 */
		_setVisible: function(b) {
			var parent = this.getParent();
			if (parent) {
				if (this.bBatchFilter) {
					domClass.toggle(parent.domNode, "csi-batch-filter-visible", b);
				} else {
					domClass.toggle(parent.domNode, "csi-filter-exists", b);
				}
			}
		},

		addClearAll: function() {
			// render action to clear all filters
			this.clearAll = new Button({
				"class": html.CSS_BUTTON_LINK,
				label: locale.resource("ClearAll"),
				onClick: () => {
					this.onClearFilters();
				}
			}).placeAt(this.buttonsContainerNode, "last");
		}
	});
});